import { router } from "@inertiajs/react";
import { Receipt } from "lucide-react";
import { ListTemplate } from "@/templates/ListTemplate";
import { Table } from "@/components/Table";
import { TablePager } from "@/components/TablePager";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";

interface AllocationRow {
  id: string;
  amountMinor: number;
  sourceName: string | null;
  status: string;
  when: string | null;
  contributionId: string;
}

interface Props {
  goal: { id: string; name: string };
  allocations: {
    rows: AllocationRow[];
    page: number;
    perPage: number;
    total: number;
    totalPages: number;
    perPageOptions: number[];
  };
}

function fmtMoney(minor: number) {
  return `£${(minor / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function GoalsAllocations({ goal, allocations }: Props) {
  return (
    <ListTemplate
      title="Allocations"
      description={`Received allocations funding ${goal.name}.`}
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Goals", to: "/goals" },
        { label: goal.name, to: `/goals/${goal.id}` },
        { label: "Allocations" },
      ]}
      footer={
        <TablePager
          pagination={{
            page: allocations.page,
            perPage: allocations.perPage,
            total: allocations.total,
            totalPages: allocations.totalPages,
            perPageOptions: allocations.perPageOptions,
            pageParam: "page",
            perPageParam: "per_page",
          }}
          reloadProps={["allocations"]}
          itemLabel={{ singular: "allocation", plural: "allocations" }}
        />
      }
    >
      {allocations.rows.length === 0 ? (
        <EmptyState
          icon={<Receipt />}
          title="No allocations yet"
          description="Contributions allocated to this goal will appear here."
        />
      ) : (
        <Table>
          <Table.Header>
            <tr>
              <Table.Head className="pl-4">When</Table.Head>
              <Table.Head>Source</Table.Head>
              <Table.Head>Status</Table.Head>
              <Table.Head className="text-right pr-4">Amount</Table.Head>
            </tr>
          </Table.Header>
          <Table.Body>
            {allocations.rows.map((a) => {
              const url = `/contributions/${a.contributionId}`;
              return (
                <Table.Row
                  key={a.id}
                  className="cursor-pointer"
                  onClick={() => router.visit(url)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") router.visit(url);
                  }}
                >
                  <Table.Cell className="pl-4 whitespace-nowrap text-fg-muted tabular">
                    {fmtDate(a.when)}
                  </Table.Cell>
                  <Table.Cell className="max-w-[18rem] truncate text-fg">{a.sourceName ?? "—"}</Table.Cell>
                  <Table.Cell>
                    <StatusBadge status={a.status} hideIcon />
                  </Table.Cell>
                  <Table.Cell className="text-right pr-4 tabular font-medium text-fg">
                    {fmtMoney(a.amountMinor)}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </ListTemplate>
  );
}
